import { Parameters } from '@app/models/Parameters';
import { post, get } from './http.api';
import { ExportRequest, PCComment } from '@app/models/export/ExportRequest';
import {
  ExportApiData,
  ExportResponse,
  UploadDocumentResponse,
  CustomerResponse,
  GetCustomerExportsResponse,
} from '@app/models/export/ExportResponse';
import { ExportTableData, ExportTableRow } from '@app/models/export/ExportResponse';
import { ExportEntityLink } from '@app/models/export/ExportResponse';

export const getExportTableData = async (parameters: Parameters): Promise<ExportTableData> => {
  try {
    const pathname = window.location.pathname;
    const pathParts = pathname.split('/');
    const rawType = pathParts[1] === 'export' ? pathParts[2] : undefined;

    let formattedType: string | undefined = undefined;

    if (rawType) {
      if (rawType === 'normal') {
        formattedType = 'NORMAL';
      } else if (rawType === 'feed-and-drugs') {
        formattedType = 'DRUG';
      }
    }

    if (formattedType) {
      localStorage.setItem('exportType', formattedType);
    }

    const queryParams = {
      ...parameters,
      ...(formattedType ? { type: formattedType } : {}),
    };

    const { data, status } = await get<any>('exports', { params: queryParams }, true);

    console.log(`Status: ${status}`, `Data:`, data);

    const exportResponse = data?.exports;

    const exports: ExportTableRow[] =
      exportResponse?.data?.map((exportData: any) => ({
        id: exportData?.id,
        code: exportData?.code,
        type: exportData?.type,
        customer_id: exportData?.customer_id,
        status: exportData?.status,
        expire_date: exportData?.expire_date,
        product_reg_type: exportData?.product_reg_type,
      })) || [];

    return {
      data: exports,
      pagination: {
        current: exportResponse?.current_page || 1,
        pageSize: exportResponse?.per_page || 10,
        total: exportResponse?.total || 0,
      },
      status_options: data?.status_options || [],
    };
  } catch (error: any) {
    console.error('Error fetching Exports:', error);
    throw error;
  }
};

export const getAllExports = async (): Promise<ExportResponse> => {
  try {
    const { data, status } = await get<ExportResponse>('exports', undefined, true);
    console.log(`Status: ${status}`, `Data: ${JSON.stringify(data)}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getExportById = async (id: number): Promise<ExportResponse> => {
  try {
    const { data, status } = await get<ExportResponse>(`exports/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${JSON.stringify(data)}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const storeExport = async (exportData: ExportRequest) => {
  try {
    const response = await post<any>('exports/create', exportData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const createExport = async (): Promise<ExportApiData> => {
  try {
    const storedType = localStorage.getItem('exportType');
    const exportType = storedType ? storedType : 'normal';
    const { data, status } = await get<ExportApiData>(
      'exports/create',
      {
        params: { type: exportType },
      },
      true,
    );

    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const showExport = async (id: number) => {
  try {
    const { data, status } = await get<ExportApiData>(`exports/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const editExport = async (id: number) => {
  try {
    const { data, status } = await get<ExportApiData>(`exports/${id}/edit`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const updateExport = async (id: number, updateExportData: ExportRequest) => {
  try {
    const { data, status } = await post<ExportRequest>(`exports/${id}/edit`, updateExportData, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
export const storeExportComment = async (id: number, commentData: PCComment) => {
  try {
    const response = await post<any>(`exports/${id}/store_comment`, commentData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const updateExportComment = async (id: number, com_id: number, commentData: PCComment) => {
  try {
    const { data, status } = await post<PCComment>(
      `exports/${id}/${com_id}/update_comment`,
      commentData,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
export const uploadDocument = async (export_id: number, formData: FormData): Promise<UploadDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadDocumentResponse>(`exports/${export_id}/upload_document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};
export interface UploadAdditionalDocumentResponse {
  data: {
    export_additional_documents: {
      name: string;
      file: string;
      document_id: string;
      document_url: string;
    }[];
  };
  message: string;
}

export const uploadAdditionalDocument = async (
  export_id: number,
  formData: FormData,
): Promise<UploadAdditionalDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadAdditionalDocumentResponse>(
      `exports/${export_id}/upload_additional`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
      console.error(`Error ${status}:`, data.errors || data.message);
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};
export const updateExportStatus = async (id: number, action: any) => {
  try {
    const { data, status } = await post<ExportRequest>(`exports/${id}/do_action`, action, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
export const generateExportInvoice = async (id: number, invoiceType: string) => {
  try {
    const response = await post<any>(`exports/${id}/create_invoice`, { invoice_type: invoiceType }, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getInvoice = async (id: number) => {
  try {
    const { data, status } = await get<any>(`/invoices/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
export interface UploadAdditionalDocumentResponse {
  data: {
    export_additional_documents: {
      name: string;
      file: string;
      document_id: string;
      document_url: string;
    }[];
  };
  message: string;
}

export const uploadAdditionalDocumentPR = async (
  export_id: number,
  formData: FormData,
): Promise<UploadAdditionalDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadAdditionalDocumentResponse>(
      `exports/${export_id}/upload_exporter_document`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
      console.error(`Error ${status}:`, data.errors || data.message);
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};
interface AttachEntitiesPayload {
  product_registration_ids: number[];
}

interface AttachEntitiesResponse {
  message: string;
  data: ExportEntityLink[];
}

export const attachEntitiesToExport = async (exportId: number, product_registration_ids: number[]) => {
  const payload = { product_registration_ids };
  const { data } = await post(`exports/${exportId}/attach_entities`, payload);
  return data;
};
export const updateServiceCharge = async (id: number, serviceData: { export_service_charges: any[] }) => {
  try {
    const response = await post<any>(`exports/${id}/update_service_charge`, serviceData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const uploadVerificationDocument = async (export_id: number, formData: FormData): Promise<any | null> => {
  try {
    const { data, status } = await post(`exports/${export_id}/upload_verification`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
      console.error(`Error ${status}:`, data.errors || data.message);
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};

export const generateExportCertificate = async (id: number) => {
  try {
    const response = await post<any>(`exports/${id}/generate_certificate`, undefined, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const uploadCertifiedDocument = async (
  export_id: number,
  formData: FormData,
): Promise<UploadDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadDocumentResponse>(`exports/${export_id}/upload_certificate`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};
